import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { LogOut, LayoutDashboard, Menu, X, ChevronDown, Calendar, Brain } from "lucide-react";

const COAT = "https://customer-assets.emergentagent.com/job_66cda0d4-d07a-4a92-8bdd-b78d3aba21d7/artifacts/xbhowqoo_ephigie-hayti.jpg";

export default function Header() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [cohortsOpen, setCohortsOpen] = useState(false);
  const cohortsRef = useRef(null);

  useEffect(() => {
    if (!cohortsOpen) return;
    const handler = (e) => {
      if (cohortsRef.current && !cohortsRef.current.contains(e.target)) setCohortsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [cohortsOpen]);

  const dashPath = !user
    ? "/login"
    : user.role === "admin" ? "/admin"
    : user.role === "trainer" ? "/trainer"
    : "/student";

  const closeMenu = () => setOpen(false);

  return (
    <header data-testid="site-header" className="glass-header sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <img src={COAT} alt="Seayisyen" className="h-9 w-9 object-contain mix-blend-screen" />
          <div className="flex flex-col leading-none">
            <span className="font-serif text-xl text-[#f5f5f5] tracking-tight group-hover:text-[#d4af37] transition-colors">Seayisyen</span>
            <span className="overline text-[9px] mt-0.5">Liberté ou la Mort</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-3 md:gap-5">
          <Link to="/cohort-info#syllabus" data-testid="nav-syllabus" className="text-sm text-neutral-400 hover:text-[#d4af37] transition-colors">
            {t("nav.syllabus")}
          </Link>

          {/* Cohorts dropdown */}
          <div className="relative" ref={cohortsRef} data-testid="nav-cohorts-wrap">
            <button
              type="button"
              data-testid="nav-cohorts-trigger"
              onClick={() => setCohortsOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-sm text-neutral-400 hover:text-[#d4af37] transition-colors"
              aria-haspopup="menu"
              aria-expanded={cohortsOpen}
            >
              {t("nav.cohorts")} <ChevronDown size={14} className={`transition-transform ${cohortsOpen ? "rotate-180" : ""}`} />
            </button>
            {cohortsOpen && (
              <div
                role="menu"
                data-testid="nav-cohorts-menu"
                className="absolute right-0 mt-3 w-72 bg-[#080808] border border-[#1f1f1f] shadow-2xl z-50"
              >
                <Link
                  to="/enroll?tier=standard"
                  onClick={() => setCohortsOpen(false)}
                  data-testid="nav-cohorts-standard"
                  className="flex items-start gap-3 px-4 py-3 hover:bg-[#d4af37]/5 border-b border-[#161616] transition-colors"
                >
                  <Calendar size={16} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm text-white">{t("nav.standardCohort")}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">{t("cohortCompare.standardStart")}</div>
                  </div>
                </Link>
                <Link
                  to="/enroll-advanced"
                  onClick={() => setCohortsOpen(false)}
                  data-testid="nav-cohorts-advanced"
                  className="flex items-start gap-3 px-4 py-3 hover:bg-[#d4af37]/5 transition-colors"
                >
                  <Brain size={16} className="text-[#e6192b] mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm text-white">{t("nav.advancedCohort")}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">{t("cohortCompare.advancedStart")}</div>
                  </div>
                </Link>
              </div>
            )}
          </div>

          <Link to="/cohort-info#pricing" data-testid="nav-pricing" className="text-sm text-neutral-400 hover:text-[#d4af37] transition-colors">
            {t("nav.pricing")}
          </Link>
          <Link to="/payment-info" data-testid="nav-payment" className="text-sm text-neutral-400 hover:text-[#d4af37] transition-colors">
            {t("nav.payment")}
          </Link>
          <Link to="/services" data-testid="nav-services" className="text-sm text-neutral-400 hover:text-[#d4af37] transition-colors">
            {t("nav.services")}
          </Link>
          <Link to="/competition" data-testid="nav-competition" className="text-sm text-[#d4af37] hover:text-white transition-colors font-condensed tracking-widest uppercase">
            {t("nav.competition")}
          </Link>
          <LanguageSwitcher />
          {user ? (
            <div className="flex items-center gap-2">
              <button
                data-testid="header-dashboard-btn"
                onClick={() => nav(dashPath)}
                className="inline-flex items-center gap-1.5 text-sm text-[#d4af37] hover:text-white transition-colors"
              >
                <LayoutDashboard size={16} /> {t("nav.dashboard")}
              </button>
              <button
                data-testid="header-logout-btn"
                onClick={async () => { await logout(); nav("/"); }}
                className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-[#e6192b] transition-colors"
                title={t("nav.logout")}
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="header-login-btn" className="text-sm font-condensed tracking-widest uppercase text-neutral-300 hover:text-[#d4af37] transition-colors">
              {t("nav.login")}
            </Link>
          )}
        </div>

        {/* Mobile burger */}
        <div className="md:hidden flex items-center gap-3">
          <LanguageSwitcher />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="text-neutral-300 hover:text-[#d4af37] transition-colors p-1"
            aria-label="Open menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <div className="gold-divider" />

      {/* Mobile slide-down panel */}
      {open && (
        <div className="md:hidden bg-[#050505] border-b border-[#161616]" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            <MobileLink to="/cohort-info#syllabus" onClick={closeMenu} testId="m-nav-syllabus" label={t("nav.syllabus")} />
            <MobileLink to="/enroll?tier=standard" onClick={closeMenu} testId="m-nav-cohort-standard" label={t("nav.standardCohort")} />
            <MobileLink to="/enroll-advanced" onClick={closeMenu} testId="m-nav-cohort-advanced" label={t("nav.advancedCohort")} />
            <MobileLink to="/cohort-info#pricing" onClick={closeMenu} testId="m-nav-pricing" label={t("nav.pricing")} />
            <MobileLink to="/payment-info" onClick={closeMenu} testId="m-nav-payment" label={t("nav.payment")} />
            <MobileLink to="/services" onClick={closeMenu} testId="m-nav-services" label={t("nav.services")} />
            <MobileLink to="/competition" onClick={closeMenu} testId="m-nav-competition" label={t("nav.competition")} accent />
            <div className="h-px bg-[#161616] my-2" />
            {user ? (
              <>
                <MobileLink to={dashPath} onClick={closeMenu} testId="m-nav-dashboard" label={t("nav.dashboard")} accent />
                <button
                  data-testid="m-nav-logout"
                  onClick={async () => { await logout(); closeMenu(); nav("/"); }}
                  className="text-left px-1 py-2.5 text-sm font-condensed tracking-widest uppercase text-neutral-400 hover:text-[#e6192b] transition-colors"
                >
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <MobileLink to="/login" onClick={closeMenu} testId="m-nav-login" label={t("nav.login")} accent />
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, label, onClick, testId, accent }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testId}
      className={`px-1 py-2.5 text-sm font-condensed tracking-widest uppercase transition-colors ${
        accent ? "text-[#d4af37]" : "text-neutral-300 hover:text-[#d4af37]"
      }`}
    >
      {label}
    </Link>
  );
}
